import type { ReactNode } from "react";
import { Calendar, MapPin, ShoppingCart, Users } from "lucide-react";
import { Badge } from "@baluu/ui/components/badge";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import { formatMoney } from "../../money";
import { Notice } from "../../parts";
import {
  attendeesLabel,
  formatDueDate,
  formatEventWhen,
  type PrivateBooking,
  type PrivateService,
  type SecureOrder,
  type SecurePaymentSchool,
} from "./securePayment";

// The secure payment page's pieces: a payable row of the plan, the
// paid-so-far bar, the private event's or service's summary box, and the
// phone's sticky Pay Now bar. Ports of v1's PaymentPlanOption,
// PaymentProgress, PrivateBookingBox, PrivateServiceBox, RelatedListItem
// and StickyPayBar.

export function PaymentPlanOption({
  name,
  amount,
  currency,
  isPaid = false,
  dueDate,
  timezone,
  onPay,
  disabled,
  starting,
}: {
  name: string;
  amount: number;
  currency: string;
  isPaid?: boolean;
  dueDate?: string;
  timezone?: string;
  onPay: () => void;
  disabled?: boolean;
  starting?: boolean;
}) {
  const due = !isPaid ? formatDueDate(dueDate, timezone) : null;
  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center md:justify-between">
      <div>
        <p className="font-semibold">{name === "" ? formatMoney(amount, currency) : `${name} - ${formatMoney(amount, currency)}`}</p>
        {due && <p className="text-sm text-muted-foreground">Due: {due}</p>}
      </div>
      {isPaid ? (
        <Badge className="self-start bg-green-100 text-green-900 md:self-center">Paid</Badge>
      ) : (
        <Button size="sm" className="w-full md:w-auto" onClick={onPay} disabled={disabled}>
          {starting && <Spinner className="mr-2 size-4" />}
          Pay Now
        </Button>
      )}
    </div>
  );
}

export function PaymentProgress({
  paid,
  total,
  percent,
  currency,
  paidInstalments,
  totalInstalments,
}: {
  paid: number;
  total: number;
  percent: number;
  currency: string;
  paidInstalments?: number;
  totalInstalments?: number;
}) {
  return (
    <div className="mb-5">
      <div className="mb-2 flex flex-wrap justify-between gap-x-4 gap-y-0.5 text-sm">
        <span>
          <strong>{formatMoney(paid, currency)}</strong> of {formatMoney(total, currency)} paid
        </span>
        {totalInstalments !== undefined && totalInstalments > 0 && (
          <span className="text-muted-foreground">
            {paidInstalments} of {totalInstalments} instalment{totalInstalments > 1 && "s"} paid
          </span>
        )}
      </div>
      <div
        className="h-1.5 overflow-hidden rounded bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        aria-label="Payment progress"
      >
        <div className="h-full rounded bg-primary" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function SchoolImage({ school }: { school: SecurePaymentSchool }) {
  const image = school.images?.[0]?.url;
  if (!image) return null;
  return (
    <div className="relative aspect-[2/1] overflow-hidden rounded-t-lg sm:aspect-[10/7]">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={image} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
    </div>
  );
}

function Row({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-center gap-4 text-sm [&:not(:last-child)]:mb-4">
      <span className="shrink-0 text-primary">{icon}</span>
      <span>{children}</span>
    </li>
  );
}

export function PrivateBookingBox({ order, booking, school }: { order: SecureOrder; booking: PrivateBooking; school: SecurePaymentSchool }) {
  const event = booking.privateCourse;
  const when = formatEventWhen(event.startDateTime, event.duration, school.timezone);
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <SchoolImage school={school} />
      <ul className="p-5 sm:p-6">
        <Row icon={<ShoppingCart className="size-6" />}>Total: {formatMoney(order.totalAmount, order.currencyUsed)}</Row>
        {when && (
          <Row icon={<Calendar className="size-6" />}>
            {when.day}
            <br />
            {when.time}
          </Row>
        )}
        <Row icon={<Users className="size-6" />}>{attendeesLabel(event.studentCount)}</Row>
        {event.address && <Row icon={<MapPin className="size-6" />}>{event.address}</Row>}
      </ul>
    </div>
  );
}

export function PrivateServiceBox({ order, service, school }: { order: SecureOrder; service: PrivateService; school: SecurePaymentSchool }) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <SchoolImage school={school} />
      <div className="space-y-2 p-5 text-sm sm:p-6">
        <p>Payment for {service.name}</p>
        <p>
          <strong>Total</strong>: {formatMoney(order.totalAmount, order.currencyUsed)}
        </p>
      </div>
    </div>
  );
}

/** The phone's Pay Now, kept in reach at the bottom of the screen. */
export function StickyPayBar({
  name,
  amount,
  currency,
  onPay,
  disabled,
}: {
  name: string;
  amount: number;
  currency: string;
  onPay: () => void;
  disabled?: boolean;
}) {
  return (
    <>
      {/* In-flow spacer, so the bar never covers the end of the page. */}
      <div className="h-[84px] sm:hidden" />
      {/* data-baluu-bottom-bar: the chat bubble stacks above it. */}
      <div
        className="fixed inset-x-0 bottom-0 z-[500] flex items-center justify-between gap-4 border-t bg-background px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))] shadow-[0_-2px_10px_rgba(0,0,0,0.08)] sm:hidden"
        data-baluu-bottom-bar
      >
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm text-muted-foreground">{name === "" ? "Amount due" : name}</span>
          <span className="text-lg font-bold">{formatMoney(amount, currency)}</span>
        </div>
        <Button size="sm" className="shrink-0" onClick={onPay} disabled={disabled}>
          Pay Now
        </Button>
      </div>
    </>
  );
}

/** After a payment the record doesn't show yet: Pay stays hidden, and the
 * customer can only ask the page to look again. */
export function PaymentPendingNotice({
  polling,
  schoolName,
  onCheck,
  checking,
}: {
  polling: boolean;
  schoolName: string;
  onCheck: () => void;
  checking: boolean;
}) {
  if (polling) {
    return <Notice tone="info">Thank you, your payment has been received. We’re confirming it now, and this page will update in a moment.</Notice>;
  }
  return (
    <Notice tone="info">
      <p>
        Your payment has been received, but it isn’t showing here yet, so please don’t pay again. Your confirmation email will have
        the details; if it doesn’t arrive, please contact {schoolName}.
      </p>
      <Button size="sm" variant="outline" className="mt-3" onClick={onCheck} disabled={checking}>
        {checking && <Spinner className="mr-2 size-4" />}
        Check again
      </Button>
    </Notice>
  );
}
